import { Modal } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

// api
import { deleteNode, markAsEndNode } from '../../../api/sequence';

// assets
import SendInbox from '../../../../../assets/customSVG/inbox/SendInbox';
import { images } from '../../../../../assets/images/index';

// constants
import {
  INITIAL_NODE_COMPONENT_DATA,
  INITIAL_TRIGGER_DATA,
  TRIGGERS_ICONS,
} from '../../../constants/sequence';
import { INITIAL_NODE_TRIGGER_DATA, ACTIONS_LIST, OPERATIONS_LIST } from '../../../constants/triggers';

// helpers
import { cn } from '../../../../../helper/cn';
import { isObjectEmpty } from '../../../helpers';
import {
  formatUpdatedBroadcast,
  getNodeCurrentStatus,
} from '../../../helpers/sequence/node';

// redux
import { updateToggleToast } from '../../../../../reduxToolkit/appSlice';
import {
  useAspDispatch,
  useAspSelector,
} from '../../../../../test/jest-redux-hooks';

// hooks
import useOutsideClick from '../../../../../hooks/useOutsideClick';
import useToggle from '../../../../../hooks/useToggle';

// components
import { ICONS } from '../../../../../assets/icons';
import EdgeHandler from '../edge/Handler';
import NodeEditMode from '../edit/Index';
import PreviewModalHeader from '../preview/PreviewModalHeader';
import PreviewNodeContainer from '../preview/PreviewNodeContainer';
import TriggerList from '../triggers/TriggerList';
import TriggerModel from '../triggers/TriggerModel';
import Actions from './Actions';
import Details from './Details';
import Handler from './Handler';
import Metrics from './Metrics';

const Nodes = ({
  nodes,
  edges,
  newEdge,
  handleMouseDown,
  handleMouseUp,
  onChange,
  handleOnMouseDownEdge,
  findEmptySpace,
  onMouseEnterEdgeEnd,
  handleEdgeMouseUp,
  allowToAddNode,
  getSelectectedNodeId,
  allowToEditFlow,
  sequenceDetails,
  profileInfo,
  selectedNode,
}) => {
  const { campaign_id, sequence_id } = useParams();
  const { currentBrand, toggleToast } = useAspSelector((state) => state.app);
  const dispatch = useAspDispatch();

  const nodeRef = useRef();
  const nodeOutsideRef = useOutsideClick(() => {});

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useToggle(null);
  const [previewData, setPreviewData] = useState(null);

  // check if the node is operation type
  const checkIsOperationType =  useCallback((node) => OPERATIONS_LIST.some((item) => item?.key === node?.type), []);

  // Node preview open/close state (default open for all)
  const [nodePreviewStates, setNodePreviewStates] = useState(
    nodes?.reduce((acc, node) => {
      if (checkIsOperationType(node)) return {
        ...acc,
        [node.uniq_id]: false
      }
      return {
        ...acc,
        [node.uniq_id]: true
      }
    }, {}) || {}
  );

  useEffect(() => {
    setNodePreviewStates(
      nodes?.reduce((acc, node) => {
        if (checkIsOperationType(node)) return {
          ...acc,
          [node.uniq_id]: false
        }
        return {
          ...acc,
          [node.uniq_id]: true
        }
      }, {}) || {}
    );
  }, [nodes]);

  // Track nodes currently playing the closing animation
  const [nodeClosingStates, setNodeClosingStates] = useState({});

  useEffect(() => {
    const handleKeyDown = (e) => e?.code === 'Escape' && handleCancelEditMode();

    if (selectedNode?.settings?.defaults?.isEditMode)
      window.addEventListener('keyup', handleKeyDown);

    return () => window.removeEventListener('keyup', handleKeyDown);
  }, [selectedNode?.settings?.defaults?.isEditMode]);

  const handleToastError = (message) =>
    dispatch(
      updateToggleToast([
        ...toggleToast,
        {
          id: toggleToast?.length + 1,
          content: message,
          status: 'Error',
          duration: '',
        },
      ])
    );

  // Toggle node preview (click) — only used for operation nodes on handler click
  const toggleNodePreview = (nodeId) => {
    setNodePreviewStates((prev) => {
      const nextOpen = !prev[nodeId];
      // If opening, clear any closing state
      if (nextOpen) {
        setNodeClosingStates((prevClose) => {
          const updated = { ...prevClose };
          delete updated[nodeId];
          return updated;
        });
      }
      return { ...prev, [nodeId]: nextOpen };
    });
  };

  const onSelectTrigger = ({ node, data }) => {
    const updatedDefaults = {
      ...node.settings.defaults,
      isExisting: true,
      isEditMode: true,
    };

    const newData = {
      ...node,
      ...INITIAL_TRIGGER_DATA[data?.key],
      label: data?.label,
      trigger_type: data?.key,
      data: INITIAL_NODE_COMPONENT_DATA[data?.key],
      settings: {
        ...node.settings,
        defaults: updatedDefaults,
      },
    };

    onChange({
      type: 'UPDATE_NODE',
      id: node?.uniq_id,
      data: newData,
    });

    onChange({
      type: 'UPDATE_DATA',
      data: {
        selectedNode: [newData],
      },
    });
  };

  // Find the previous node in the sequence based on edges
  const findPreviousNode = useCallback(
    (currentNode) => {
      if (!currentNode?.uniq_id || !edges?.length) return null;

      // Find the edge where current node is the target
      const incomingEdge = edges.find(
        (edge) => edge.end_id === currentNode.uniq_id
      );
      if (!incomingEdge) return null;

      // Find the source node of the edge
      return (
        nodes.find((node) => node.uniq_id === incomingEdge.start_id) || null
      );
    },
    [edges, nodes]
  );

  const onTriggerClick = async ({ data, node, label }) => {
    const prevNode = findPreviousNode(node);
    const template_subcategory_type = prevNode?.data?.subcategory_type;
    let hasFlowButton = false;

    if (prevNode?.data?.type === 'template') {
      const template = prevNode?.data?.template;
      const buttonsSection = template?.find(item => item.type === 'BUTTONS');
      const buttons = buttonsSection?.buttons || [];
      hasFlowButton = buttons.some(button => button.type === 'FLOW');
    } else {
      hasFlowButton = prevNode?.component_type === 'FLOW';
    }

    let currentNode = {
      ...node,
      label: hasFlowButton ? 'Flow Completed' : (template_subcategory_type === 'ORDER_DETAIL' ? (label || data?.label) : data?.headerText),
      type: (data?.key === 'PAYMENT_CAPTURED' || data?.key === 'PAYMENT_FAILED' || data?.key === 'FLOW_COMPLETED') ? 'TEXT' : data?.key,
      // type: data?.key ,
      data: INITIAL_NODE_TRIGGER_DATA[data?.key],
    };

    onChange({
      type: 'UPDATE_DATA',
      data: { selectedNode: [currentNode] },
    });
  };

  const toggleNodeEditMode = async ({
    id,
    value,
    uniqId,
    defaults,
    key,
    node = null,
  }) => {
    if (node) {
      let data = {
        ...node,
        settings: {
          ...node?.settings,
          defaults: { ...node?.settings?.defaults, isEditMode: true },
        },
      };

      if (node?.type === 'TRIGGER' && !node?.settings?.defaults?.isExisting)
        data = formatUpdatedBroadcast({ node: data });

      onChange({
        type: 'UPDATE_DATA',
        data: { selectedNode: [data] },
      });
    } else {
      if (key && key?.length > 0) {
        onChange({
          type: 'UPDATE_NODE',
          id: uniqId,
          primaryKey: 'settings',
          secondaryKey: 'defaults',
          key,
          data: value,
        });
      } else {
        onChange({
          type: 'UPDATE_NODE',
          id: uniqId,
          primaryKey: 'settings',
          secondaryKey: 'defaults',
          key: 'isEditMode',
          data: value,
        });
      }
    }
  };

  const deleteEdges = useCallback(
    (id) => {
      const deleteEdges = edges?.filter(
        (edge) => edge?.end_id !== id && edge?.start_id !== id
      );

      onChange({
        type: 'UPDATE_DATA',
        data: { edges: deleteEdges },
      });
    },
    [edges]
  );

  const handleDeleteNode = async ({ uniq_id, type, order, node }) => {
    const payload = {
      brandId: currentBrand?.brand_id,
      campaignId: campaign_id,
      nodeType: type,
    };

    const res = await deleteNode(sequence_id, uniq_id, payload);
    if (res?.status === 200) {
      onChange({
        type: 'DELETE_NODE',
        id: uniq_id,
        key: order,
      });
      deleteEdges(uniq_id);
      // Clean up preview/closing state when node is deleted
      setNodePreviewStates(prev => {
        const newState = { ...prev };
        delete newState[uniq_id];
        return newState;
      });
      setNodeClosingStates(prev => {
        const newState = { ...prev };
        delete newState[uniq_id];
        return newState;
      });
    }
  };

  const onEdgeMouseDown = ({ node, connection_point, position, payload }) => {
    handleOnMouseDownEdge({ node, connection_point, position, payload });
  };

  const checkIsEdgeConnected = useCallback(
    ({ start_node_id, connection_point }) => {
      const isConnected = edges?.find(
        (edge) =>
          edge?.start_id === start_node_id &&
          edge?.connection_point?.id === connection_point?.id
      );

      return isConnected ? true : false;
    },
    [edges, nodes]
  );

  const toggleLastNode = async ({ nodeId, value }) => {
    try {
      const payload = {
        brand_id: currentBrand?.brand_id,
        campaign_id: campaign_id,
        end: value,
      };
      const res = await markAsEndNode(sequence_id, nodeId, payload);

      if (res?.status === 200) {
        onChange({
          type: 'TOGGLE_LAST_NODE',
          id: nodeId,
          value,
        });
      } else {
        handleToastError(res?.response?.data?.message);
      }
    } catch (error) {
      console.warn(error?.message);
    }
  };

  const toggleSelectedNode = (node, uniq_id, value) => {
    // only allow preview-open-on-click for operation nodes
    const isOperationType = OPERATIONS_LIST.some((item) => item?.key === node?.type);
    getSelectectedNodeId(uniq_id);
    if (allowToEditFlow && !isOperationType)
      onChange({
        type: 'UPDATE_NODE_DATA',
        id: uniq_id,
        primaryKey: 'settings',
        secondaryKey: 'defaults',
        key: 'isSelected',
        data: value,
      });
  };

  const checkIsEndNodeConnected = useCallback(
    ({ nodeId }) => {
      const res = edges?.filter((edge) => edge?.end_id === nodeId);
      return res?.length > 0 ? true : false;
    },
    [edges]
  );

  const checkIsStartNodeConnected = useCallback(
    ({ nodeId }) => {
      const res = edges?.filter((edge) => edge?.start_id === nodeId);
      return res?.length > 0 ? true : false;
    },
    [edges]
  );

  const getUnSavedEdge = useCallback(
    async (id) => await edges?.find((edge) => edge?.end_id === id),
    [edges]
  );

  const removeEdges = useCallback(
    (startNodeId) => {
      const res = edges?.filter((edge) => edge?.start_id !== startNodeId);
      onChange({
        type: 'UPDATE_DATA',
        data: { edges: res },
      });
    },
    [edges]
  );

  const updateEdgesCoordinates = useCallback(
    ({ startNodeId, removedEdges, data }) => {
      let updatedEdges = edges;

      // Filter out the removed edges once, before any further processing
      if (removedEdges?.length > 0) {
        updatedEdges = updatedEdges.filter(
          (edge) => !removedEdges.includes(edge?.uniq_id)
        );
      }

      const currentNodeUpdatedEdges = [];

      const res = updatedEdges.map((edge) => {
        if (edge?.start_id === startNodeId) {
          // Updating the edge coordinates
          const updatedEdge = {
            ...edge,
            coordinates: {
              ...edge.coordinates,
              start: {
                ...edge.coordinates.start,
                y: edge.coordinates.start.y + Number(data?.height),
              },
            },
          };

          currentNodeUpdatedEdges.push({
            id: updatedEdge.uniq_id,
            coordinates: updatedEdge.coordinates,
          });

          return updatedEdge;
        }

        return edge;
      });

      return { edges: res, currentNodeUpdatedEdges };
    },
    [edges]
  );

  // Close with bounce-out, then fully hide
  const closeNode = (nodeId) => {
    // Play closing animation
    setNodeClosingStates((prev) => ({ ...prev, [nodeId]: true }));
    // Mark as closed
    setNodePreviewStates((prev) => ({ ...prev, [nodeId]: false }));
    // After animation ends, clear closing flag
    setTimeout(() => {
      setNodeClosingStates((prev) => {
        const updated = { ...prev };
        delete updated[nodeId];
        return updated;
      });
    }, 340); // keep in sync with CSS animation duration
  };

  const handleCancel = () => setIsPreviewModalOpen(false);

  const handleCancelEditMode = useCallback(() => {
    onChange({
      type: 'UPDATE_DATA',
      data: { selectedNode: null },
    });
  }, [selectedNode]);

  const edgeFrom  = useCallback(() => {
    return edges?.find((edge) => edge?.status === 'UN_SAVED');
  }, [edges]);

  return (
    <>
      {nodes?.map((node) => {
        const isConnected = checkIsEndNodeConnected({ nodeId: node?.uniq_id });
        const isOperationConnected = checkIsStartNodeConnected({
          nodeId: node?.uniq_id,
        });
        const isEdgeConnected = checkIsStartNodeConnected({
          nodeId: node?.uniq_id,
        });

        const nodeCurrentStatus = getNodeCurrentStatus(node);

        // flags for preview visibility & animation
        const forcedOpen = node?.status === 'UN_SAVED'; // keep unsaved visible
        const isPreviewOpen = forcedOpen || !!nodePreviewStates[node?.uniq_id];
        const isClosing = !!nodeClosingStates[node?.uniq_id];

        const nodeItem =
          OPERATIONS_LIST.find((item) => item?.key === node?.type) ||
          ACTIONS_LIST.find((item) => item?.key === node?.type);

        const color = node?.is_trigger
          ? TRIGGERS_ICONS[node?.label]?.bg
          : nodeItem?.bg || '#0acd95';

        const isOperationType = OPERATIONS_LIST.some((item) => item?.key === node?.type);

        return (
          <div
            key={node?.id}
            className={cn(
              'w-24 h-auto text-white flex items-center gap-2.5 z-0'
            )}
            style={{
              zIndex: node?.uniq_id === selectedNode ? 999 : 10,
              pointerEvents: 'all',
              visibility: 'visible',
              position: 'absolute',
              transform: `translate(${node?.coordinates?.x}px, ${node?.coordinates?.y}px)`,
            }}
            onMouseEnter={(e) =>
              toggleSelectedNode(node, node?.uniq_id, true)
            }
            onMouseLeave={(e) => toggleSelectedNode(node, null, false)}
            ref={nodeOutsideRef}
          >
            <div className="relative w-full h-full flex flex-1 items-start z-0">
              {/* node handler */}
              <div
                className={cn(
                  'group relative w-24 h-auto text-white flex flex-col items-center justify-center gap-2.5 !z-[1]'
                )}
              >
                {/* Nodes toolbar */}
                {node?.data && (
                  <Actions
                    sequenceDetails={sequenceDetails}
                    node={node}
                    newEdge={newEdge}
                    setPreviewData={setPreviewData}
                    allowToEditFlow={allowToEditFlow}
                    setIsPreviewModalOpen={setIsPreviewModalOpen}
                    isConnected={isConnected}
                    isEdgeConnected={isEdgeConnected}
                    toggleLastNode={toggleLastNode}
                    toggleNodeEditMode={toggleNodeEditMode}
                    handleDeleteNode={handleDeleteNode}
                  />
                )}

                {/* handler */}
                <div ref={nodeRef} className="relative flex items-center">
                  {!node?.is_trigger && (
                    <div
                      className={cn(
                        'absolute z-[2]',
                        node?.status !== 'UN_SAVED'
                          ? node?.is_last_node
                            ? 'top-[25.8%] -left-2'
                            : 'top-[33%] -left-2'
                          : 'top-[33%] -left-2'
                      )}
                    >
                      <EdgeHandler
                        type="end"
                        node={node}
                        onEdgeMouseEnter={() => {
                          if (allowToEditFlow) onMouseEnterEdgeEnd({ node });
                        }}
                        handleEdgeMouseUp={({ node, position }) => {
                          if (handleEdgeMouseUp && allowToEditFlow)
                            handleEdgeMouseUp({ node, position });
                        }}
                        color={color}
                        allowToEditFlow={allowToEditFlow}
                      />
                    </div>
                  )}
                  <div
                    onMouseDown={(e) => {
                      if (allowToEditFlow) handleMouseDown(e, node?.uniq_id);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Open/close preview only if the node.type is in OPERATIONS_LIST
                      if (isOperationType) {
                        toggleNodePreview(node?.uniq_id);
                      }
                    }}
                    style={{
                      width: node?.data ? (node?.is_trigger ? 110 : 86) : 86,
                      cursor: 'pointer',
                    }}
                  >
                    <Handler
                      id={node?.id}
                      uniqId={node?.uniq_id}
                      node_type={node?.node_type}
                      is_trigger={node?.is_trigger}
                      is_last_node={node?.is_last_node}
                      trigger_type={node?.trigger_type}
                      component_type={node?.component_type}
                      label={
                        node?.label
                          ? (node?.label || nodeItem?.headerText)
                          : node?.is_trigger
                          ? 'Select Trigger'
                          : 'Add Node'
                      }
                      order={node?.order}
                      data={node.data}
                      settings={node?.settings}
                      isEditMode={node?.isEditMode}
                      status={node?.status}
                      onChange={onChange}
                      allowToEditFlow={allowToEditFlow}
                      handleMouseUp={handleMouseUp}
                      handleEdgeMouseUp={({ event, node, position }) => {
                        if (handleEdgeMouseUp && allowToEditFlow)
                          handleEdgeMouseUp({ event, node, position });
                      }}
                      newEdge={newEdge}
                      bgColor={
                        nodeCurrentStatus
                          ? nodeCurrentStatus?.color
                          : !isObjectEmpty(node?.data)
                          ? node?.is_trigger
                            ? TRIGGERS_ICONS[node?.label]?.bg
                            : nodeItem?.bg || '#0acd95'
                          : '#6940F2'
                      }
                    />
                  </div>

                  {/* right side point - for operations and non-trigger nodes */}
                  {(OPERATIONS_LIST.map((item) => item?.key).includes(node?.type)) && node?.type !== 'ASSIGNEE' && (
                    <div
                      className={cn(
                        'absolute z-[2] -right-[0.5rem]',
                        node?.status !== 'UN_SAVED' && node?.is_last_node
                          ? 'top-[25.8%]'
                          : 'top-[33%]'
                      )}
                    >
                      <EdgeHandler
                        type="start"
                        isConnected={isOperationConnected}
                        node={node}
                        onEdgeMouseDown={({ position, payload }) => {
                          if (!isOperationConnected) {
                            onEdgeMouseDown?.({
                              node,
                              connection_point: {
                                id: node?.settings?.connections?.points?.find(
                                  (cp) => cp?.type === 'next_steps'
                                )?.id,
                                type: 'next_steps',
                              },
                              position,
                              payload: {
                                ...payload,
                                nodeId: node?.uniq_id,
                                type: 'next_steps',
                              },
                            });
                          }
                        }}
                        color={color}
                        allowToEditFlow={allowToEditFlow}
                        draggable={true}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* node preview */}
              <div
                className={cn(
                  'absolute top-0 !z-0 transition-all duration-500 ease-out',
                  newEdge ? 'cursor-grabbing' : 'cursor-default',
                  node?.data
                    ? node?.is_trigger
                      ? 'left-[125%]'
                      : 'left-[115%]'
                    : 'left-[115%]',
                  // OPEN: show & bounce in
                  isPreviewOpen
                    ? 'opacity-100 translate-x-0 scale-100 pointer-events-auto bounce-in'
                    // CLOSING: keep visible while playing bounce-out
                    : isClosing
                    ? 'opacity-100 translate-x-0 scale-100 pointer-events-auto bounce-out'
                    // CLOSED: fully hidden
                    : 'opacity-0 -translate-x-4 scale-95 pointer-events-none hidden'
                )}
                id={node?.uniq_id}
                onMouseDown={(e) =>
                  toggleSelectedNode(node, node?.uniq_id, true)
                }
                onMouseUp={(e) =>
                  toggleSelectedNode(node, node?.uniq_id, false)
                }
              >
                <div 
                  className={cn(
                    'absolute flex flex-col items-center transition-all duration-500 ease-out delay-100',
                    isPreviewOpen
                      ? 'transform-gpu translate-y-0 opacity-100'
                      : 'transform-gpu translate-y-2 opacity-0'
                  )}
                >
                  {!allowToEditFlow && !(OPERATIONS_LIST.map((item) => item?.key).includes(node?.type)) && (
                    <div
                      className={cn(
                        'transition-all duration-500 ease-out delay-200',
                        isPreviewOpen
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 translate-y-1'
                      )}
                      style={{
                        position: 'absolute',
                        top: '-45px',
                        left: '50%',
                        transform: `translate(-50%, -45px)`,
                        zIndex: 3,
                      }}
                    >
                      <Metrics metrics={node?.metrics} type={node?.type} nodeId={node?.uniq_id} />
                    </div>
                  )}

                  {node?.type && node?.data && !isObjectEmpty(node?.data) ? (
                    <div 
                      className={cn(
                        'transition-all duration-500 ease-out delay-150',
                        isPreviewOpen
                          ? 'opacity-100 scale-100 translate-y-0'
                          : 'opacity-0 scale-95 translate-y-2'
                      )}
                    >
                      <PreviewNodeContainer
                        node={node}
                        bgColor={
                          nodeCurrentStatus
                            ? nodeCurrentStatus?.color
                            : node?.is_trigger
                            ? TRIGGERS_ICONS[node?.label]?.bg
                            : nodeItem?.bg || '#0ACD95'
                        }
                        closeNode={closeNode}
                      >
                        <Details
                          previewType={'node'}
                          node={node}
                          newEdge={newEdge}
                          onChange={onChange}
                          onEdgeMouseDown={({
                            connection_point,
                            position,
                            payload,
                          }) =>
                            onEdgeMouseDown({
                              node,
                              connection_point,
                              position: position,
                              payload,
                            })
                          }
                          checkIsEdgeConnected={checkIsEdgeConnected}
                          isConnected={isConnected}
                          allowToEditFlow={allowToEditFlow}
                          nodeCurrentStatus={nodeCurrentStatus}
                          findEmptySpace={findEmptySpace}
                          closeNode={closeNode}
                        />
                      </PreviewNodeContainer>
                    </div>
                  ) : (
                    <div 
                      className={cn(
                        'w-full text-black relative transition-all duration-500 ease-out delay-100',
                        isPreviewOpen
                          ? 'opacity-100 scale-100 translate-y-0'
                          : 'opacity-0 scale-95 translate-y-2'
                      )}
                    >
                      {node?.is_trigger &&
                      !node?.settings?.defaults?.isEditMode ? (
                        <div className="relative w-full min-w-96">
                          <TriggerList
                            onSelectTrigger={(data) =>
                              onSelectTrigger({ node, data })
                            }
                          />
                        </div>
                      ) : (
                        <div className="rounded-2xl shadow-[0px_4px_30px_0px_#2D30361A] backdrop-blur-sm">
                          <TriggerModel
                            type="tab"
                            edgeFrom={edgeFrom()?.settings?.type}
                            onTriggerClick={(data) => {
                              onTriggerClick({ node, data, label: data?.label });
                            }}
                            allow={true}
                            previousNodeType={findPreviousNode(node)}
                            findPreviousNode={findPreviousNode}
                            node={node}
                            edges={edges}
                            nodes={nodes}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <Modal
        open={selectedNode?.settings?.defaults?.isEditMode}
        afterOpenChange={(open) => {
          if (allowToEditFlow)
            toggleNodeEditMode({
              id: selectedNode?.id,
              uniqId: selectedNode?.uniq_id,
              value: open ? 'true' : 'false',
              defaults: selectedNode?.settings?.defaults,
            });
        }}
        styles={{ content: { padding: 0 } }}
        centered
        footer={null}
        closable={false}
        wrapClassName={'bg-[#00000035] !p-0 !m-0'}
        className={'w-full max-w-[450px] !rounded-2xl !overflow-hidden'}
        destroyOnClose={true}
      >
        <NodeEditMode
          originalData={previewData}
          node={selectedNode}
          onChange={onChange}
          onTriggerClick={onTriggerClick}
          findEmptySpace={findEmptySpace}
          allowToAddNode={allowToAddNode}
          toggleNodeEditMode={toggleNodeEditMode}
          toggleLastNode={toggleLastNode}
          allowToEditFlow={allowToEditFlow}
          removeEdges={removeEdges}
          updateEdgesCoordinates={updateEdgesCoordinates}
          handleCancelEditMode={handleCancelEditMode}
          getUnSavedEdge={getUnSavedEdge}
          sequenceDetails={sequenceDetails}
          findPreviousNode={findPreviousNode}
        />
      </Modal>
      <Modal
        open={isPreviewModalOpen}
        styles={{ content: { padding: 0 } }}
        afterOpenChange={(open) => {
          setIsPreviewModalOpen(open);
        }}
        centered
        footer={null}
        closable={false}
        wrapClassName={'bg-[#00000035] !p-0 !m-0'}
        className={'!w-full max-w-[375px] !h-fit !rounded-2xl !overflow-hidden'}
        destroyOnClose={true}
      >
        <div className={''}>
          <PreviewModalHeader
            icon={profileInfo?.profile_picture_url || ''}
            title={
              profileInfo?.send_from_phone_name ||
              profileInfo?.account_name ||
              ''
            }
            handleCancel={handleCancel}
            showClose
          />
          <div className="" style={{ backgroundImage: `url(${images?.bg})` }}>
            <div className="w-fit min-h-[50dvh] max-h-[70dvh] py-2.5 px-4 overflow-y-scroll">
              <Details
                previewMode={true}
                previewType={'node'}
                node={previewData}
                checkIsEdgeConnected={checkIsEdgeConnected}
                isConnected={checkIsEndNodeConnected({
                  nodeId: previewData?.uniq_id,
                })}
                allowToShowEdge={false}
                color={
                  getNodeCurrentStatus()
                    ? getNodeCurrentStatus()?.color
                    : '#0ACD95'
                }
                allowToEditFlow={allowToEditFlow}
                nodeCurrentStatus={getNodeCurrentStatus()}
              />
            </div>
            <div
              className={cn(
                'w-full flex-1 px-4 flex items-center h-16 bg-white'
              )}
            >
              <div
                className={cn(
                  'w-full h-9 flex items-center gap-2.5 px-2.5 border border-[var(--border-100)] rounded-full'
                )}
              >
                <img
                  src={ICONS?.step3EmojiIcon}
                  alt="emoji"
                  className="size-5"
                />
                <p
                  className={cn(
                    'w-full flex-1 text-sm weight-small cursor-not-allowed'
                  )}
                >
                  {previewData?.type === 'TRIGGER' &&
                  previewData?.trigger_type === 'CAMP_QR_URL'
                    ? previewData?.data?.url_text
                    : null}
                </p>

                <SendInbox color={'var(--font-600)'} width={18} height={18} />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Bounce-in/out animation for preview open/close */}
      <style>{`
        @keyframes nodes-bounce-in {
          0% {
            opacity: 0;
            transform: translateX(-0.5rem) translateY(0.5rem) scale(0.95);
          }
          60% {
            opacity: 1;
            transform: translateX(0) translateY(-2px) scale(1.04);
          }
          80% {
            transform: translateY(0) scale(0.98);
          }
          100% {
            transform: translateY(0) scale(1);
          }
        }
        .bounce-in {
          animation: nodes-bounce-in 320ms ease-out both;
        }

        @keyframes nodes-bounce-out {
          0% {
            opacity: 1;
            transform: translateX(0) translateY(0) scale(1);
          }
          20% {
            transform: translateY(-2px) scale(1.02);
          }
          100% {
            opacity: 0;
            transform: translateX(-0.5rem) translateY(0.5rem) scale(0.95);
          }
        }
        .bounce-out {
          animation: nodes-bounce-out 320ms ease-in both;
        }
      `}</style>
    </>
  );
};

export default Nodes;
